import { useState, useEffect, useCallback } from 'react';
import { Bot, Loader2, Save, KeyRound, AlertCircle, CheckCircle2, TestTube, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import {
  getPlatformLLMConfig,
  updatePlatformLLMConfig,
  testPlatformLLMConfig,
  PlatformLLMConfig,
  PlatformLLMConfigInput,
  LLMProvider,
} from '@/services/platformLLMApi';

const providerPresets: Record<LLMProvider, { baseUrl: string; models: string[] }> = {
  groq: {
    baseUrl: 'https://api.groq.com/openai/v1',
    models: ['llama-3.3-70b-versatile', 'llama-3.1-8b-instant', 'mixtral-8x7b-32768'],
  },
  ollama: {
    baseUrl: 'http://localhost:11434/v1',
    models: ['llama3.2', 'mistral', 'codellama'],
  },
  together: {
    baseUrl: 'https://api.together.xyz/v1',
    models: ['meta-llama/Meta-Llama-3.1-70B-Instruct-Turbo'],
  },
  anthropic: {
    baseUrl: '',
    models: ['claude-sonnet-4-20250514', 'claude-3-5-sonnet-20241022', 'claude-3-5-haiku-20241022'],
  },
  openai: {
    baseUrl: 'https://api.openai.com/v1',
    models: ['gpt-4o', 'gpt-4o-mini'],
  },
};

interface FormState {
  name: string;
  provider: LLMProvider;
  model: string;
  baseUrl: string;
  apiKey: string;
  temperature: number;
  maxTokens: number;
  isActive: boolean;
  sequentialMode: boolean;
}

const emptyForm: FormState = {
  name: 'Platform Default (Groq)',
  provider: 'groq',
  model: 'llama-3.3-70b-versatile',
  baseUrl: 'https://api.groq.com/openai/v1',
  apiKey: '',
  temperature: 0.7,
  maxTokens: 4096,
  isActive: true,
  sequentialMode: false,
};

export default function PlatformLLMPage() {
  const [config, setConfig] = useState<PlatformLLMConfig | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const c = await getPlatformLLMConfig();
      setConfig(c);
      if (c) {
        setForm({
          name: c.name,
          provider: c.provider,
          model: c.model,
          baseUrl: c.baseUrl ?? '',
          apiKey: '',
          temperature: c.temperature,
          maxTokens: c.maxTokens,
          isActive: c.isActive,
          sequentialMode: c.sequentialMode,
        });
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load platform LLM config';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleProviderChange = (provider: LLMProvider) => {
    const preset = providerPresets[provider];
    setForm((f) => ({
      ...f,
      provider,
      baseUrl: preset.baseUrl,
      model: preset.models[0] ?? '',
    }));
  };

  const handleTest = async () => {
    if (!form.model) {
      setError('Le modèle est requis pour tester');
      return;
    }
    setTesting(true);
    setTestResult(null);
    setError(null);
    try {
      const result = await testPlatformLLMConfig({
        provider: form.provider,
        model: form.model,
        baseUrl: form.baseUrl || null,
        // Si vide, le backend retombe sur la cle stockee (apres dechiffrement)
        apiKey: form.apiKey || null,
      });
      setTestResult({
        success: result.success,
        message: result.success
          ? `Connexion réussie (${result.latencyMs}ms)`
          : result.message,
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Test failed';
      setTestResult({ success: false, message: msg });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // Note : si apiKey est laissee vide ('') on garde l'existante (cf
      // platformLLMController.updatePlatformLLMConfig). Pour wiper, l'admin
      // utiliserait null mais l'UI ne l'expose pas (volontaire : pas de geste
      // accidentel destructeur).
      const payload: PlatformLLMConfigInput = {
        name: form.name,
        provider: form.provider,
        model: form.model,
        baseUrl: form.baseUrl || null,
        temperature: form.temperature,
        maxTokens: form.maxTokens,
        isActive: form.isActive,
        sequentialMode: form.sequentialMode,
      };
      if (form.apiKey.trim().length > 0) {
        payload.apiKey = form.apiKey;
      }
      const updated = await updatePlatformLLMConfig(payload);
      setConfig(updated);
      setForm((f) => ({ ...f, apiKey: '' }));
      toast.success('Platform LLM config saved');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Save failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const preset = providerPresets[form.provider];

  return (
    <div className="max-w-3xl mx-auto">
      <header className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Bot className="w-7 h-7 text-indigo-600" />
          <h1 className="text-2xl font-semibold text-gray-900">Platform LLM</h1>
        </div>
        <p className="text-sm text-gray-600">
          Configuration IA partagée par toutes les sandboxes (<code className="font-mono text-xs bg-gray-100 px-1 rounded">enterprise_id IS NULL</code>).
          Quand un user de sandbox lance une requête IA, c'est cette clef qui est utilisée — et le quota est décrémenté.
          Les enterprises payantes utilisent leur propre config (réglée dans <code className="font-mono text-xs bg-gray-100 px-1 rounded">/settings?tab=llm</code>) et ne consomment pas ce crédit.
        </p>
      </header>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {!config && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            Aucune config plateforme n'existe. Renseignez les champs ci-dessous puis cliquez Enregistrer pour la créer.
          </p>
        </div>
      )}

      {config && (
        <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-start gap-2">
          {config.hasApiKey ? (
            <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          )}
          <div className="text-sm text-emerald-900 flex-1">
            <p className="font-medium">
              {config.hasApiKey
                ? 'Clé API configurée — les sandboxes peuvent utiliser l\'IA.'
                : 'Clé API manquante — les sandboxes ne peuvent pas utiliser l\'IA tant que vous ne la renseignez pas.'}
            </p>
            <p className="text-xs text-emerald-700 mt-1">
              Dernière modification : {new Date(config.updatedAt).toLocaleString('fr-FR')}
            </p>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
            <select
              value={form.provider}
              onChange={(e) => handleProviderChange(e.target.value as LLMProvider)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {(Object.keys(providerPresets) as LLMProvider[]).map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Modèle</label>
            <input
              type="text"
              value={form.model}
              onChange={(e) => setForm({ ...form, model: e.target.value })}
              list="models-suggestions"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <datalist id="models-suggestions">
              {preset.models.map((m) => <option key={m} value={m} />)}
            </datalist>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Base URL</label>
          <input
            type="text"
            value={form.baseUrl}
            onChange={(e) => setForm({ ...form, baseUrl: e.target.value })}
            placeholder={preset.baseUrl || '(provider default)'}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
            <KeyRound className="w-4 h-4" />
            Clé API
            {config?.hasApiKey && (
              <span className="text-xs text-emerald-600 font-normal">(actuellement configurée)</span>
            )}
          </label>
          <input
            type="password"
            value={form.apiKey}
            onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
            placeholder={config?.hasApiKey ? 'Laisser vide pour conserver la clé existante' : 'Coller la clé API ici'}
            autoComplete="off"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono"
          />
          <p className="text-xs text-gray-500 mt-1">
            Chiffrée AES-256-GCM avant stockage. Jamais renvoyée en clair par l'API.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Temperature</label>
            <input
              type="number"
              step="0.1"
              min="0"
              max="2"
              value={form.temperature}
              onChange={(e) => setForm({ ...form, temperature: parseFloat(e.target.value) || 0 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max tokens</label>
            <input
              type="number"
              min="1"
              max="200000"
              value={form.maxTokens}
              onChange={(e) => setForm({ ...form, maxTokens: parseInt(e.target.value, 10) || 4096 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        <div className="flex items-center gap-6 pt-2">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            Actif
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={form.sequentialMode}
              onChange={(e) => setForm({ ...form, sequentialMode: e.target.checked })}
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            Mode séquentiel (LLMs lents)
          </label>
        </div>

        {testResult && (
          <div
            className={`p-3 rounded-lg flex items-start gap-2 text-sm ${
              testResult.success
                ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}
            role="status"
          >
            {testResult.success ? (
              <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
            ) : (
              <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            )}
            <span className="flex-1 font-mono text-xs leading-relaxed">{testResult.message}</span>
          </div>
        )}

        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <button
            onClick={handleTest}
            disabled={testing || saving || !form.model}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            title={
              !form.apiKey && !config?.hasApiKey
                ? 'Renseignez une clé pour tester (ou configurez-en une d\'abord)'
                : !form.apiKey
                  ? 'Test avec la clé actuellement stockée'
                  : 'Test avec la clé saisie ci-dessus'
            }
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <TestTube className="w-4 h-4" />}
            {testing ? 'Test en cours...' : 'Tester la connexion'}
          </button>
          <button
            onClick={handleSave}
            disabled={saving || testing}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}
